import { PALETTE } from '../tokens';
import { Sparkline } from './Sparkline';
import { coherenceLabel } from '../utils/coherenceLabels';

// Display-layer labels — single source in utils/coherenceLabels.js.
const STATUS_LABELS = {
  CALIBRATING: coherenceLabel('CALIBRATING'),
  FRAGMENTED: coherenceLabel('FRAGMENTED'),
  STABILIZING: coherenceLabel('STABILIZING'),
  STABLE_COHERENCE: coherenceLabel('STABLE_COHERENCE'),
  REGRESSING: coherenceLabel('REGRESSING'),
};

/**
 * SignalScore — hero score display for the coherence dashboard.
 *
 * Props:
 *   score          — integer 0–100 (null if unavailable)
 *   coherenceStatus — one of CoherenceStatusEnum values
 *   sparklineData  — array of 0–100 integers (last 7 days)
 *   previousScore  — previous session score for change framing (nullable)
 *   daysInApp      — total snapshot count / days since first check-in
 *   loading        — show skeleton
 *   error          — API error occurred
 */
export function SignalScore({
  score,
  coherenceStatus,
  sparklineData = [],
  previousScore = null,
  daysInApp = 0,
  loading = false,
  error = false,
}) {
  if (loading) return <SignalScoreSkeleton />;
  if (error) return <SignalScoreError />;

  const isCalibrating =
    coherenceStatus === 'CALIBRATING' || daysInApp < 7 || score === null;

  if (isCalibrating) {
    const daysRemaining = Math.max(1, 7 - daysInApp);
    return (
      <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: PALETTE.primaryMid,
            fontWeight: 500,
            marginBottom: 12,
          }}
        >
          Calibrating
        </div>
        <p
          style={{
            fontSize: 13.5,
            color: PALETTE.textMid,
            lineHeight: 1.72,
            fontWeight: 300,
            maxWidth: 280,
            margin: '0 auto',
          }}
        >
          Your signal is still forming. {daysRemaining} more day{daysRemaining !== 1 ? 's' : ''}.
        </p>
      </div>
    );
  }

  const statusLabel = STATUS_LABELS[coherenceStatus] || 'Scattered';
  const trendUp =
    sparklineData.length >= 2
      ? sparklineData[sparklineData.length - 1] >= sparklineData[0]
      : true;

  // Score change framing
  let changeText = null;
  if (previousScore !== null && previousScore !== score) {
    if (score > previousScore) {
      changeText = `Signal up from ${previousScore}`;
    } else if (score < previousScore) {
      changeText = 'Signal dipped this week';
    }
  } else if (previousScore !== null && previousScore === score) {
    changeText = 'Holding steady';
  }

  return (
    <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
      {/* Score number */}
      <div
        className="serif"
        style={{
          fontSize: 72,
          fontWeight: 400,
          fontStyle: 'italic',
          color: PALETTE.text,
          lineHeight: 1,
          letterSpacing: '-0.02em',
        }}
      >
        {score}
      </div>

      {/* Change framing */}
      {changeText && (
        <div
          style={{
            fontSize: 11,
            color: PALETTE.textFaint,
            fontWeight: 300,
            marginTop: 6,
          }}
        >
          {changeText}
        </div>
      )}

      {/* Coherence status */}
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: PALETTE.primaryMid,
          fontWeight: 500,
          marginTop: 10,
        }}
      >
        {statusLabel}
      </div>

      {/* 7-day sparkline */}
      {sparklineData.length >= 2 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 14,
          }}
        >
          <Sparkline data={sparklineData} up={trendUp} />
        </div>
      )}
    </div>
  );
}

function SignalScoreSkeleton() {
  return (
    <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
      <div
        style={{
          width: 80,
          height: 64,
          background: PALETTE.surfaceAlt,
          borderRadius: 12,
          margin: '0 auto 12px',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          width: 100,
          height: 12,
          background: PALETTE.surfaceAlt,
          borderRadius: 6,
          margin: '0 auto 10px',
        }}
      />
      <div
        style={{
          width: 68,
          height: 10,
          background: PALETTE.surfaceAlt,
          borderRadius: 6,
          margin: '0 auto',
        }}
      />
    </div>
  );
}

function SignalScoreError() {
  return (
    <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
      <div
        style={{
          fontSize: 13,
          color: PALETTE.textMute,
          fontWeight: 300,
        }}
      >
        Signal unavailable
      </div>
    </div>
  );
}
